import React from "react";
import { getFileExt, isImage, isPdf, filenameFromUrl } from "@/lib/fileHelpers";
import { getEvidenceUrl, refFromValue } from "@/lib/evidenceAccess";

/**
 * Convert a stored evidence value (raw storage key, evidence record ID, or a
 * legacy fully-qualified URL) into a value the server-side broker understands.
 *
 * ACTION 2: the browser no longer signs anything. Every read is brokered by the
 * `evidence-access` Edge Function, which re-runs the Action 1 authorization
 * chain and derives the storage key server-side.
 */
export function getEvidenceStoragePath(value: string): string | null {
  if (!value) return null;

  if (!/^https?:\/\//i.test(value)) {
    return value.replace(/^\/+/, "");
  }

  const objectMatch = value.match(/\/storage\/v1\/object\/(?:public|sign)\/evidence\/(.+?)(?:\?|$)/);
  return objectMatch?.[1] ? decodeURIComponent(objectMatch[1]) : null;
}

export function getEvidenceCanonicalKey(value: string): string {
  const storagePath = getEvidenceStoragePath(value);
  return storagePath ? `storage:${storagePath}` : `url:${value.trim()}`;
}

export async function resolveEvidenceUrl(value: string): Promise<string | null> {
  if (!value) return null;

  const storagePath = getEvidenceStoragePath(value);
  if (storagePath) {
    try {
      return await getEvidenceUrl(refFromValue(storagePath));
    } catch (error) {
      console.error("[EvidencePreview] evidence-access failed for", storagePath, error);
      return null;
    }
  }

  // External (non-storage) link kept as-is.
  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  return null;
}


type ResolvedItem = {
  original: string;
  url: string | null;
  name: string;
  ext: string;
};

export function EvidencePreview({ urls }: { urls: string[] }) {
  const [items, setItems] = React.useState<ResolvedItem[] | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    if (!urls?.length) {
      setItems([]);
      return;
    }
    setItems(null);
    (async () => {
      const resolved = await Promise.all(
        urls.map(async (raw) => {
          const url = await resolveEvidenceUrl(raw);
          return {
            original: raw,
            url,
            name: filenameFromUrl(raw),
            ext: getFileExt(raw),
          };
        })
      );
      if (!cancelled) setItems(resolved);
    })();
    return () => {
      cancelled = true;
    };
  }, [JSON.stringify(urls)]);

  if (!urls?.length) {
    return <div className="text-sm text-slate-500">No evidence uploaded.</div>;
  }

  if (items === null) {
    return <div className="text-sm text-slate-500">Loading evidence…</div>;
  }

  return (
    <div className="space-y-4">
      {items.map((item, idx) => {
        const { url, name, ext } = item;
        if (!url) {
          return (
            <div key={idx} className="rounded-xl border p-3 text-sm text-slate-500">
              {name} — unavailable (could not generate access link)
            </div>
          );
        }
        if (isImage(ext)) {
          return (
            <div key={idx} className="rounded-xl border p-3">
              <div className="mb-2 text-xs text-slate-500">{name}</div>
              <img src={url} alt={name} className="max-h-[360px] w-auto rounded-lg object-contain" />
              <div className="mt-2 text-right">
                <a className="text-sm text-slate-700 underline" href={url} target="_blank" rel="noreferrer">Open original</a>
              </div>
            </div>
          );
        }
        if (isPdf(ext)) {
          return (
            <div key={idx} className="rounded-xl border p-3">
              <div className="mb-2 text-xs text-slate-500">{name}</div>
              <embed src={url} type="application/pdf" className="h-[420px] w-full rounded-lg" />
              <div className="mt-2 text-right">
                <a className="text-sm text-slate-700 underline" href={url} target="_blank" rel="noreferrer">Open in new tab</a>
              </div>
            </div>
          );
        }
        return (
          <div key={idx} className="flex items-center justify-between rounded-xl border p-3">
            <div className="truncate pr-4 text-sm text-slate-700">{name}</div>
            <a className="rounded-md border px-3 py-1 text-sm text-slate-700 hover:bg-slate-50" href={url} target="_blank" rel="noreferrer">
              Open
            </a>
          </div>
        );
      })}
    </div>
  );
}

/**
 * Public helper so other components (e.g. ReviewerConsole's "Open N" footer
 * buttons) can resolve a raw evidence path/URL on click.
 */
export async function openEvidenceInNewTab(value: string) {
  const url = await resolveEvidenceUrl(value);
  if (url) window.open(url, "_blank", "noopener,noreferrer");
}
